/**
 * The store × article sales history and forecast basis — pure helpers (bata-ui#166 W4-3/W4-4,
 * `docs/adr/6101`; DEC-76, `docs/adr/6120`).
 *
 * **The window is the URL's or the platform's — never this front end's** (CLAUDE.md §3.4).
 * `?salesFrom=` and `?salesTo=` are the two ends, both ISO dates, given together; `?salesBy=size`
 * splits each week by size. With neither end the read names no window and the PLATFORM applies its
 * governed default, `inseason.evidence.sales_history_weeks` (DEC-76: a registry row, four weeks,
 * the planner's to tune), and says which window it read in `window_basis`. This module computes no
 * window of its own: the screen states the one the platform used, and a planner who wants another
 * picks a preset or two dates, which are written to the address so the history is a shareable link.
 *
 * The parse is pure and reads a string, never `window`, so a test asserts on a string and an
 * export resolver can be handed the search.
 */

import { ApiProblemError } from '@/api';
import type { ForecastBasisSummary, SalesHistory, SalesHistoryWeek } from '@/api/generated';

export const SALES_FROM_PARAM = 'salesFrom';
export const SALES_TO_PARAM = 'salesTo';
export const SALES_BY_PARAM = 'salesBy';
export const SALES_BY_SIZE = 'size';

/**
 * The widest window the platform's read admits (bata-platform ADR 6101,
 * `SALES_HISTORY_MAX_DAYS`): two ISO years and a week. Mirrored so an over-wide window is a
 * designed state here rather than a 422 from the service.
 */
export const SALES_HISTORY_MAX_DAYS = 742;

/** The presets a planner can choose, in weeks ending today. Never applied without a click. */
export const SALES_WINDOW_PRESET_WEEKS = [13, 26, 52] as const;

const MS_PER_DAY = 86_400_000;
const DAYS_PER_WEEK = 7;
const ISO_DATE = /^\d{4}-\d{2}-\d{2}$/;

export type SalesWindow =
  /** Neither end: the platform's governed default window applies (DEC-76). */
  | { readonly state: 'governed'; readonly bySize: boolean }
  | { readonly state: 'invalid'; readonly from: string | null; readonly to: string | null }
  | { readonly state: 'set'; readonly from: string; readonly to: string; readonly bySize: boolean };

/** An ISO calendar date that names a real day, or `null`. `2026-02-30` is not one. */
export function isoDateOrNull(raw: string | null): string | null {
  const value = raw?.trim() ?? '';
  if (!ISO_DATE.test(value)) return null;
  const parsed = new Date(`${value}T00:00:00Z`);
  return Number.isNaN(parsed.getTime()) || parsed.toISOString().slice(0, 10) !== value ? null : value;
}

function epochDay(iso: string): number {
  return Date.parse(`${iso}T00:00:00Z`) / MS_PER_DAY;
}

/**
 * The window the address states. Both ends absent is `governed` — the read names no window and
 * the platform applies its governed default. One end absent, an unparseable date, an inverted
 * window or one wider than the platform admits is `invalid`, and no request.
 */
export function readSalesWindow(search: string): SalesWindow {
  const params = new URLSearchParams(search);
  const rawFrom = params.get(SALES_FROM_PARAM);
  const rawTo = params.get(SALES_TO_PARAM);
  const bySize = params.get(SALES_BY_PARAM) === SALES_BY_SIZE;
  if ((rawFrom ?? '').trim() === '' && (rawTo ?? '').trim() === '') return { state: 'governed', bySize };
  const from = isoDateOrNull(rawFrom);
  const to = isoDateOrNull(rawTo);
  if (from === null || to === null) return { state: 'invalid', from, to };
  const span = epochDay(to) - epochDay(from);
  if (span < 0 || span >= SALES_HISTORY_MAX_DAYS) return { state: 'invalid', from, to };
  return { state: 'set', from, to, bySize };
}

/** The read's query for a window the address states: no `from`/`to` when it is the platform's. */
export function salesHistoryQuery(
  storeKey: string,
  articleKey: string,
  scope: Exclude<SalesWindow, { state: 'invalid' }>,
): { store_key: string; article_key: string; by: 'size' | 'total'; from?: string; to?: string } {
  const by = scope.bySize ? 'size' : 'total';
  if (scope.state === 'governed') return { store_key: storeKey, article_key: articleKey, by };
  return { store_key: storeKey, article_key: articleKey, from: scope.from, to: scope.to, by };
}

// ── the window the platform read ────────────────────────────────────────────

/** The problem the platform answers when no window was named and none is governed (ADR 6120). */
export const WINDOW_NOT_SET_CODE = 'planning.inseason.sales_history_window_not_set';
const WINDOW_INVALID_CODE = 'planning.inseason.sales_history_window_invalid';

/**
 * Why a read without a window drew no history: `not-set` — the platform governs the default and no
 * row is in force for this market; `not-served` — the running platform predates DEC-76 and requires
 * a window (a request-validation 422, not the platform's own window refusal). Anything else is an
 * ordinary failure, `null`.
 */
export function windowUnavailable(error: unknown): 'not-set' | 'not-served' | null {
  if (!(error instanceof ApiProblemError)) return null;
  if (error.code === WINDOW_NOT_SET_CODE) return 'not-set';
  if (error.status === 422 && error.code !== WINDOW_INVALID_CODE && !error.code.startsWith('planning.')) return 'not-served';
  return null;
}

const NOT_SET_REASON_KEY: Readonly<Record<string, string>> = {
  parameter_not_set: 'inseason.salesHistory.defaultReason.notSet',
  parameter_differs_between_markets: 'inseason.salesHistory.defaultReason.differs',
  parameter_value_invalid: 'inseason.salesHistory.defaultReason.invalid',
};
export const UNRECOGNISED_NOT_SET_REASON_KEY = 'inseason.salesHistory.defaultReason.unrecognised';

/** The catalogue key for the platform's `reason` on a window-not-set refusal. */
export function toNotSetReasonKey(error: unknown): string {
  const reason = error instanceof ApiProblemError ? error.problem['reason'] : undefined;
  return typeof reason === 'string' ? NOT_SET_REASON_KEY[reason] ?? UNRECOGNISED_NOT_SET_REASON_KEY : UNRECOGNISED_NOT_SET_REASON_KEY;
}

export interface WindowStatement {
  /** Whose window: the planner's (`caller`) or the platform's governed default (`parameter`). */
  readonly source: 'caller' | 'parameter';
  readonly key: string;
  readonly values: Readonly<Record<string, string | number>>;
}

/**
 * The sentence that states which window the platform read and why — its own words, never a window
 * computed here. A platform older than DEC-76 publishes no `window_basis`; its window was always the
 * caller's.
 */
export function windowStatement(history: SalesHistory): WindowStatement {
  const range = { from: history.window_start, to: history.window_end };
  const basis = (history as { readonly window_basis?: SalesHistory['window_basis'] }).window_basis;
  if (basis === undefined || basis.source === 'caller') return { source: 'caller', key: 'inseason.salesHistory.window.caller', values: range };
  const weeks = basis.weeks ?? 0;
  const changeSet = basis.parameter_set_id ?? '';
  if (basis.anchor === 'request_day') {
    return { source: 'parameter', key: 'inseason.salesHistory.window.governedNothingLanded', values: { ...range, weeks, changeSet } };
  }
  return { source: 'parameter', key: 'inseason.salesHistory.window.governed', values: { ...range, weeks, changeSet } };
}

/** The address with the window replaced; `null` removes that end. Everything else is kept. */
export function salesWindowSearch(search: string, from: string | null, to: string | null): URLSearchParams {
  const next = new URLSearchParams(search);
  if (from === null) next.delete(SALES_FROM_PARAM);
  else next.set(SALES_FROM_PARAM, from);
  if (to === null) next.delete(SALES_TO_PARAM);
  else next.set(SALES_TO_PARAM, to);
  return next;
}

/** The address with the grain replaced. `total` is the absence of the parameter. */
export function salesGrainSearch(search: string, bySize: boolean): URLSearchParams {
  const next = new URLSearchParams(search);
  if (bySize) next.set(SALES_BY_PARAM, SALES_BY_SIZE);
  else next.delete(SALES_BY_PARAM);
  return next;
}

/** The local calendar date of `now`, ISO. */
export function localIsoDate(now: Date): string {
  const shifted = new Date(Date.UTC(now.getFullYear(), now.getMonth(), now.getDate()));
  return shifted.toISOString().slice(0, 10);
}

/**
 * A preset: `weeks` whole weeks ending on `today`. Computed only when the planner chooses it,
 * and written to the address as two dates — the URL states the window, never "the last N".
 */
export function trailingWeeksWindow(today: Date, weeks: number): { readonly from: string; readonly to: string } {
  const to = localIsoDate(today);
  const start = new Date(Date.parse(`${to}T00:00:00Z`) - (weeks * DAYS_PER_WEEK - 1) * MS_PER_DAY);
  return { from: start.toISOString().slice(0, 10), to };
}

// ── what the platform's codes mean, as catalogue keys ───────────────────────

const COVERAGE_KEY: Readonly<Record<string, string>> = {
  landed: 'inseason.salesHistory.coverage.landed',
  partial: 'inseason.salesHistory.coverage.partial',
  not_landed: 'inseason.salesHistory.coverage.notLanded',
};
export const UNRECOGNISED_COVERAGE_KEY = 'inseason.salesHistory.coverage.unrecognised';

export function toCoverageKey(state: string): string {
  return COVERAGE_KEY[state] ?? UNRECOGNISED_COVERAGE_KEY;
}

const BASIS_REASON_KEY: Readonly<Record<string, string>> = {
  not_recorded_by_run: 'inseason.forecastBasis.reason.notRecordedByRun',
  forecast_run_not_archived: 'inseason.forecastBasis.reason.forecastRunNotArchived',
  horizon_not_authored: 'inseason.forecastBasis.reason.horizonNotAuthored',
  no_proposal_model_version: 'inseason.forecastBasis.reason.noProposalModelVersion',
  no_rated_position: 'inseason.forecastBasis.reason.noRatedPosition',
  // docs/adr/6121: the run recorded its tunables — one had no value in force, or the stage
  // that wrote the run does not read it; a reforecast that held no trailing window.
  not_in_force: 'inseason.forecastBasis.reason.notInForce',
  not_read_by_stage: 'inseason.forecastBasis.reason.notReadByStage',
  trailing_window_not_used: 'inseason.forecastBasis.reason.trailingWindowNotUsed',
};
export const UNRECOGNISED_BASIS_REASON_KEY = 'inseason.forecastBasis.reason.unrecognised';

export function toBasisReasonKey(reason: string | null): string {
  return reason === null ? UNRECOGNISED_BASIS_REASON_KEY : BASIS_REASON_KEY[reason] ?? UNRECOGNISED_BASIS_REASON_KEY;
}

const RATE_METHOD_KEY: Readonly<Record<string, string>> = {
  pull_expected_lead_time_demand_per_cover_period: 'inseason.forecastBasis.method.pullLeadTimeDemand',
};
export const UNRECOGNISED_RATE_METHOD_KEY = 'inseason.forecastBasis.method.unrecognised';

export function toRateMethodKey(method: string): string {
  return RATE_METHOD_KEY[method] ?? UNRECOGNISED_RATE_METHOD_KEY;
}

/** The label and unit each recorded parameter is read with. An unknown key is still shown. */
const PARAMETER_PRESENTATION: Readonly<Record<string, { readonly labelKey: string; readonly unitKey: string; readonly fractionDigits: number }>> = {
  'inseason.pull.service_target': { labelKey: 'inseason.forecastBasis.parameter.serviceTarget', unitKey: 'inseason.forecastBasis.unit.quantile', fractionDigits: 2 },
  'inseason.pull.central_level': { labelKey: 'inseason.forecastBasis.parameter.centralLevel', unitKey: 'inseason.forecastBasis.unit.quantile', fractionDigits: 2 },
  'inseason.pull.review_periods': { labelKey: 'inseason.forecastBasis.parameter.reviewPeriods', unitKey: 'inseason.forecastBasis.unit.weeks', fractionDigits: 0 },
  // The DEFAULT store lead time, in whole days (owner ruling 2026-10-14); read only for a store the
  // master gives no lead time. The weeks key is retired and kept for runs before the ruling.
  'inseason.pull.lead_time_days': { labelKey: 'inseason.forecastBasis.parameter.leadTimeDays', unitKey: 'inseason.forecastBasis.unit.days', fractionDigits: 0 },
  'inseason.pull.lead_time_periods': { labelKey: 'inseason.forecastBasis.parameter.leadTimePeriods', unitKey: 'inseason.forecastBasis.unit.weeks', fractionDigits: 0 },
};
const UNRECOGNISED_PARAMETER = { labelKey: 'inseason.forecastBasis.parameter.unrecognised', unitKey: 'inseason.forecastBasis.unit.none', fractionDigits: 2 };

export function parameterPresentation(key: string): { readonly labelKey: string; readonly unitKey: string; readonly fractionDigits: number } {
  return PARAMETER_PRESENTATION[key] ?? UNRECOGNISED_PARAMETER;
}

/**
 * The PULL rate's parameters, in the platform's order. The horizon is not one of them: it is the
 * archived reforecast's (`inseason.reforecast.horizon_weeks`), and is shown beside that run.
 */
export function basisParameters(basis: ForecastBasisSummary): ForecastBasisSummary['pull_parameters'] {
  return basis.pull_parameters;
}

// ── the history, read ───────────────────────────────────────────────────────

export interface SalesHistoryTotals {
  /** Units sold over the weeks that landed (wholly or in part); `null` when none did. */
  readonly soldUnits: number | null;
  /** Units returned over the weeks the returns feed landed; `null` when none did. */
  readonly returnedUnits: number | null;
  readonly weeks: number;
  readonly notLandedWeeks: number;
  readonly partialWeeks: number;
}

/**
 * The window's totals, over the article rows (`size_code === null`) or, when the read was split
 * by size, over every row. An absence is `null` — never zero (ADR 0014): a window in which no
 * week landed has an unknown total, not an empty one.
 */
export function salesHistoryTotals(weeks: readonly SalesHistoryWeek[]): SalesHistoryTotals {
  const starts = new Set(weeks.map((week) => week.week_start));
  const perWeek = new Map<string, SalesHistoryWeek>();
  for (const week of weeks) if (!perWeek.has(week.week_start)) perWeek.set(week.week_start, week);
  const sold = weeks.filter((week) => week.sold_units !== null);
  const returned = weeks.filter((week) => week.returned_units !== null);
  const coverage = [...perWeek.values()];
  return {
    soldUnits: sold.length === 0 ? null : sold.reduce((sum, week) => sum + (week.sold_units ?? 0), 0),
    returnedUnits: returned.length === 0 ? null : returned.reduce((sum, week) => sum + (week.returned_units ?? 0), 0),
    weeks: starts.size,
    notLandedWeeks: coverage.filter((week) => week.sales_coverage === 'not_landed').length,
    partialWeeks: coverage.filter((week) => week.sales_coverage === 'partial').length,
  };
}

/**
 * The weekly rate of sale the evidence window measures, and how many weeks measured it.
 *
 * **Only the weeks that landed WHOLLY count**, on both sides of the division. A `partial` week
 * holds the landed days' units alone (`SalesHistoryWeek`), so counting it would divide a part of a
 * week's sales by a whole week and read as a slowdown that did not happen; a `not_landed` week is
 * not a week of nought sales (ADR 0014). `null` where no week landed wholly — an unknown rate,
 * never nought, and a cover computed from nought would be infinite.
 *
 * The window itself is the platform's governed one (`inseason.evidence.sales_history_weeks`,
 * DEC-76 — four weeks, the planner's to tune), so this is "the weekly rate over the evidence
 * window", not "over four weeks": the caller states the count this returns rather than assuming it.
 * Pure.
 */
export interface WeeklyRateOfSale {
  /** Units a week over the weeks that landed wholly. */
  readonly unitsPerWeek: number;
  /** How many weeks that is the average of — never nought. */
  readonly weeks: number;
  /** Units sold over those weeks. */
  readonly soldUnits: number;
}

export function weeklyRateOfSale(weeks: readonly SalesHistoryWeek[] | undefined): WeeklyRateOfSale | null {
  // `undefined` rather than a crash: this reads a body off the wire, and a 200 whose shape is not
  // the one the generated client promises would otherwise white-screen the page it is on. No rate
  // is the same answer as no landed week — an absence, never a nought.
  if (weeks === undefined) return null;
  // The article's own rows, where the read was split by size: a size row and its article row would
  // otherwise count the same sale twice.
  const article = weeks.filter((week) => week.size_code === null);
  const rows = article.length > 0 ? article : weeks;
  const whole = new Map<string, number>();
  for (const week of rows) {
    if (week.sales_coverage !== 'landed' || week.sold_units === null) continue;
    whole.set(week.week_start, (whole.get(week.week_start) ?? 0) + week.sold_units);
  }
  if (whole.size === 0) return null;
  const soldUnits = [...whole.values()].reduce((sum, units) => sum + units, 0);
  return { unitsPerWeek: soldUnits / whole.size, weeks: whole.size, soldUnits };
}

/**
 * Units sold per size over the weeks that landed WHOLLY — the same weeks `weeklyRateOfSale` counts,
 * so the sizes add up to the «Sold» KPI. Read from a `by=size` history.
 *
 * `null` where no week landed wholly: every size's sales are then unknown, never nought. Otherwise
 * a size with no row sold nought — the platform emits a size only where it sold or was returned
 * somewhere in the window (ADR 6101), so a size it does not name sold nothing in the landed weeks.
 * Pure.
 */
export interface SoldBySize {
  /** Whole weeks counted. */
  readonly weeks: number;
  /** Units sold over every size, the sizes not on the caller's axis included. */
  readonly total: number;
  /** Units sold in one size, by its code and scale (`null` scale matches any). */
  readonly of: (code: string, scale: string | null) => number;
}

export function soldBySize(weeks: readonly SalesHistoryWeek[] | undefined): SoldBySize | null {
  if (weeks === undefined) return null;
  const whole = new Set<string>();
  const sizes: { readonly code: string; readonly scale: string | null; units: number }[] = [];
  for (const week of weeks) {
    if (week.sales_coverage !== 'landed') continue;
    whole.add(week.week_start);
    if (week.size_code === null || week.sold_units === null) continue;
    const held = sizes.find((size) => size.code === week.size_code && size.scale === week.size_scale);
    if (held === undefined) sizes.push({ code: week.size_code, scale: week.size_scale, units: week.sold_units });
    else held.units += week.sold_units;
  }
  if (whole.size === 0) return null;
  return {
    weeks: whole.size,
    total: sizes.reduce((sum, size) => sum + size.units, 0),
    of: (code, scale) => sizes
      .filter((size) => size.code === code && (scale === null || size.scale === null || size.scale === scale))
      .reduce((sum, size) => sum + size.units, 0),
  };
}

/**
 * Weeks of cover: units divided by the weekly rate of sale. `null` where the rate is unknown or
 * nought — which is a different finding from infinite cover, and the answer
 * `planning.replenishment.pull.weeks_of_cover` gives for the same case. Pure.
 *
 * **Not the `weeks_of_cover` the stock ledger publishes.** That one divides the door's COMMITTED
 * units (on hand + in transit + on order) by the FORECAST rate; this divides the units the caller
 * names by what the door actually sold. Two measures, two numerators, two denominators — so they
 * are two figures with two names, never one rendered under the other's label.
 */
export function weeksOfCoverAtRate(units: number | null, rate: WeeklyRateOfSale | null): number | null {
  if (units === null || rate === null || rate.unitsPerWeek <= 0) return null;
  return units / rate.unitsPerWeek;
}

const DECIMAL = /^(-?)(\d+)(?:\.(\d+))?$/;

/** A decimal string as a scaled integer, or `null` when it is not a plain decimal. */
function scaled(amount: string): { readonly units: bigint; readonly scale: number } | null {
  const match = DECIMAL.exec(amount.trim());
  if (match === null) return null;
  const fraction = match[3] ?? '';
  const units = BigInt(`${match[2] ?? '0'}${fraction}`);
  return { units: match[1] === '-' ? -units : units, scale: fraction.length };
}

/**
 * One currency's net value over the window. The platform publishes each amount as exact decimal
 * text; the weeks are summed exactly (as scaled integers) and converted to a number once, at the
 * end, for display. An amount that is not a plain decimal is added as a number.
 */
export function netValueByCurrency(weeks: readonly SalesHistoryWeek[]): readonly { readonly currency: string; readonly amount: number }[] {
  const exact = new Map<string, { units: bigint; scale: number }>();
  const loose = new Map<string, number>();
  for (const week of weeks) {
    for (const money of week.net_value) {
      const value = scaled(money.amount);
      if (value === null) {
        loose.set(money.currency_code, (loose.get(money.currency_code) ?? 0) + Number(money.amount));
        continue;
      }
      const held = exact.get(money.currency_code) ?? { units: 0n, scale: 0 };
      const scale = Math.max(held.scale, value.scale);
      const units = held.units * 10n ** BigInt(scale - held.scale) + value.units * 10n ** BigInt(scale - value.scale);
      exact.set(money.currency_code, { units, scale });
    }
  }
  const currencies = new Set([...exact.keys(), ...loose.keys()]);
  return [...currencies].sort((a, b) => a.localeCompare(b)).map((currency) => {
    const held = exact.get(currency);
    const exactAmount = held === undefined ? 0 : Number(held.units) / 10 ** held.scale;
    return { currency, amount: exactAmount + (loose.get(currency) ?? 0) };
  });
}
